import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Language = "en" | "ar";
type LanguageContextValue = { language: Language; isArabic: boolean; setLanguage: (language: Language) => void; toggleLanguage: () => void };

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => localStorage.getItem("kidix_language") === "ar" ? "ar" : "en");
  const setLanguage = (next: Language) => { localStorage.setItem("kidix_language", next); setLanguageState(next); };
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.body.dataset.language = language;
  }, [language]);
  const value = useMemo(() => ({ language, isArabic: language === "ar", setLanguage, toggleLanguage: () => setLanguage(language === "ar" ? "en" : "ar") }), [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}

export function localized<T extends Record<string, unknown>>(item: T, key: string, isArabic: boolean): string {
  const arabic = item[`${key}Ar`];
  const english = item[key];
  return String(isArabic && arabic ? arabic : english ?? "");
}
